import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { Modal, ModalFooter } from '../../../components/Shared/Modal';
import { FormField } from '../../../components/Shared/FormField';
import { TextArea } from '../../../components/Shared/TextArea';
import { Badge } from '../../../components/Shared/Badge';
import { calcPositionTotals } from './InvoicePositionEditor';
import { formatMoney } from '@/utils/money';
import { parseDecimal, toInputDecimal } from './formatters';
import type { FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto } from '../../../api/generated/models';

type InvoicePositionDto = FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto;

/** Raw form state — numeric fields stay strings so partial input ("2,") is not clobbered. */
interface InvoicePositionFormData {
  title: string;
  description: string;
  quantity: string;
  unit: string;
  unitNetPrice: string;
  vatRate: string;
}

/** Parsed, validated values handed to the page on save. */
export interface InvoicePositionFormValues {
  title: string;
  description: string;
  quantity: number;
  unit: string;
  unitNetPrice: number;
  vatRate: number;
}

interface InvoicePositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  saving: boolean;
  /** Position being edited; null/undefined means "add". */
  initial?: InvoicePositionDto | null;
  /** Pre-fills the VAT rate for new positions (the invoice's tax rate). */
  defaultVatRate: number;
  onSave: (values: InvoicePositionFormValues) => void | Promise<void>;
}

const toForm = (
  initial: InvoicePositionDto | null | undefined,
  defaultVatRate: number,
): InvoicePositionFormData => ({
  title: initial?.title ?? '',
  description: initial?.description ?? '',
  quantity: initial?.quantity != null ? toInputDecimal(initial.quantity) : '1',
  unit: initial?.unit ?? 'Stk',
  unitNetPrice: toInputDecimal(initial?.unitNetPrice),
  vatRate: toInputDecimal(initial?.vatRate ?? defaultVatRate),
});

export function InvoicePositionModal({
  isOpen, onClose, saving, initial, defaultVatRate, onSave,
}: InvoicePositionModalProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const isEdit = !!initial;
  const [form, setForm] = useState<InvoicePositionFormData>(
    () => toForm(initial, defaultVatRate),
  );
  const [titleTouched, setTitleTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(toForm(initial, defaultVatRate));
      setTitleTouched(false);
    }
  }, [isOpen, initial, defaultVatRate]);

  const update = <K extends keyof InvoicePositionFormData>(
    key: K, value: InvoicePositionFormData[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const quantity = parseDecimal(form.quantity);
  const unitNetPrice = parseDecimal(form.unitNetPrice);
  const vatRate = parseDecimal(form.vatRate);

  // NaN fails every comparison, so empty input counts as invalid here …
  const titleMissing = form.title.trim() === '';
  const unitMissing = form.unit.trim() === '';
  const quantityInvalid = !(quantity > 0);
  const priceInvalid = !(unitNetPrice >= 0);
  const vatInvalid = !(vatRate >= 0 && vatRate <= 100);
  const canSave = !titleMissing && !unitMissing
    && !quantityInvalid && !priceInvalid && !vatInvalid;

  // … but a message only appears once the user typed something (or left the title).
  const titleError = titleTouched && titleMissing
    ? t('invoices:positions.validation.titleRequired') : undefined;
  const quantityError = form.quantity !== '' && quantityInvalid
    ? t('invoices:positions.validation.quantityPositive') : undefined;
  const priceError = form.unitNetPrice !== '' && priceInvalid
    ? t('invoices:positions.validation.unitNetPriceMin') : undefined;
  const vatError = form.vatRate !== '' && vatInvalid
    ? t('invoices:positions.validation.vatRange') : undefined;

  const preview = !quantityInvalid && !priceInvalid && !vatInvalid
    ? calcPositionTotals([{
        id: 'preview', title: '', description: '', unit: '',
        quantity, unitNetPrice, vatRate,
      }])
    : null;

  const source = (initial?.sourceType ?? '').toLowerCase();

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      title: form.title.trim(),
      description: form.description.trim(),
      quantity,
      unit: form.unit.trim(),
      unitNetPrice,
      vatRate,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('invoices:positions.edit') : t('invoices:positions.add')}
      size="lg"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmText={t('common:actions.save')}
          confirmDisabled={!canSave}
          loading={saving}
          variant="primary"
        />
      }
    >
      <div className="space-y-4">
        {(source === 'recurring' || source === 'adhoc') && (
          <div>
            <Badge variant={source === 'recurring' ? 'info' : 'success'}>
              {t(`invoices:positionSource.${source}`)}
            </Badge>
          </div>
        )}
        <FormField
          label={t('invoices:positions.fields.title')}
          required
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          onBlur={() => setTitleTouched(true)}
          error={titleError}
          maxLength={200}
        />
        <TextArea
          label={t('invoices:positions.fields.description')}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          rows={2}
          maxLength={4000}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('invoices:positions.fields.quantity')}
            required
            inputMode="decimal"
            value={form.quantity}
            onChange={(e) => update('quantity', e.target.value)}
            error={quantityError}
            className="tabular-nums"
          />
          <FormField
            label={t('invoices:positions.fields.unit')}
            required
            value={form.unit}
            onChange={(e) => update('unit', e.target.value)}
            maxLength={20}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('invoices:positions.fields.unitNetPrice')}
            required
            inputMode="decimal"
            value={form.unitNetPrice}
            onChange={(e) => update('unitNetPrice', e.target.value)}
            error={priceError}
            className="tabular-nums"
          />
          <FormField
            label={t('invoices:positions.fields.vatRate')}
            required
            inputMode="decimal"
            value={form.vatRate}
            onChange={(e) => update('vatRate', e.target.value)}
            error={vatError}
            className="tabular-nums"
          />
        </div>

        {/* Live preview of the line totals */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-1">
          <PreviewRow
            label={t('invoices:positions.fields.lineNet')}
            value={preview ? formatMoney(preview.net) : '—'}
          />
          <PreviewRow
            label={t('invoices:manualCreate.totals.vat')}
            value={preview ? formatMoney(preview.vat) : '—'}
          />
          <PreviewRow
            label={t('invoices:positions.fields.lineGross')}
            value={preview ? formatMoney(preview.gross) : '—'}
            bold
          />
        </div>
      </div>
    </Modal>
  );
}

function PreviewRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between text-[13px] ${bold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

export default InvoicePositionModal;
